if (!customElements.get('gift-box-checkbox-component')) {
  customElements.define(
    'gift-box-checkbox-component',
    class GiftBoxCheckbox extends HTMLElement {
      cartItems; // cart-items component
      items = []; // cart items array consisting of line-items
      item_count = 0; // cart item count reference
      variantIds = []; // cart variant ids
      constructor() {
        super();
      }

      connectedCallback() {
        this.initGiftBoxToggle();
      }

      /**
       * Toogle the giftbox checked state and saved it to local storage saving user preference
       *
       * @param {boolean} checked - flag for user preference
       */
      async setGiftBoxPreference(wantGiftBox = true) {
        try {
          if (!wantGiftBox) {
            // Checkbox is checked = user DOESN'T want gift box -> remove addons
            localStorage.removeItem('want_gift_box');
            await this.removeAddons(this.variantIds);
          } else {
            // Checkbox is unchecked = user WANTS gift box -> add addons
            localStorage.setItem('want_gift_box', '1');
            await this.addAddons(this.variantIds);
          }

          return true;
        } catch (e) {
          throw e;
        }
      }

      initGiftBoxToggle() {
        // WHAT THIS DOES:
        // Manages the gift box checkbox toggle in the cart. When the user checks/unchecks the checkbox,
        // it automatically adds or removes gift box addon products from the cart and saves their preference.
        // Default state: checkbox is checked (user doesn't want gift box). When unchecked, gift box items are added.

        const checkbox = document.getElementById('cart-addon-checkbox');
        this.cartItems = document.querySelector('cart-items');

        const variantEls = document.querySelectorAll('.addon-product-id');
        if (!checkbox || !variantEls.length) return;

        const variantIds = Array.from(variantEls).map((el) => String(el.value));

        this.variantIds = variantIds;

        // --- Set initial state ---
        // Default: checkbox is checked = user doesn't want gift box
        // If user previously unchecked (wants gift box), we save 'want_gift_box' = '1'
        const wantGiftBox = localStorage.getItem('want_gift_box') === '1';
        checkbox.checked = !wantGiftBox;

        // Sync cart contents on page load based on saved preference
        (async () => {
          try {
            // Synchronize cart items to match the user's saved preference
            if (wantGiftBox) {
              await this.ensureAddonsAdded(variantIds);
              // add toast
              // CartUtils.showCartToast(
              //   window.toastNotifications?.receivedGiftbox,
              //   'success',
              // );
            } else {
              await this.ensureAddonsRemoved(variantIds);
            }
          } catch (e) {
            console.error('[giftbox] initial sync error', e);
          }
        })();

        // --- Handle checkbox toggle ---
        checkbox.addEventListener('change', async (e) => {
          checkbox.disabled = true;
          try {
            await this.setGiftBoxPreference(!e.target.checked);
          } catch (err) {
            console.error('[giftbox] toggle error', err);
            // Rollback the checkbox state if the operation fails
            checkbox.checked = !checkbox.checked;
            alert(
              'Sorry, we failed to update your gift box. Please try again.',
            );
          } finally {
            checkbox.disabled = false;
          }
        });
      }

      async getCart() {
        return fetch('cart.js')
          .then((res) => res.json())
          .then((data) => {
            const cart_items = data.items || [];
            const itemCount = data.item_count || 0;

            this.items = cart_items; // updates the cart items array
            this.item_count = itemCount; // updates item_count variable
            return data;
          })
          .catch((e) => {
            console.error('Error Fetching Cart items: ' + e);
            CartUtils.showCartToast(
              e?.message || window.toastNotifications?.errorOccured,
              'error',
            );
          });
      }

      async removeAddons(variantIds = []) {
        try {
          const cart = await this.getCart();
          const toRemove = cart.items
            .filter((it) => variantIds.includes(String(it.variant_id)))
            .map((it) => it.key);
          if (!toRemove.length) return;

          for (const key of toRemove) await this.removeByKey(key);
        } catch (e) {
          console.error('Error removing addons: ' + e);
        }
      }

      async addAddons(variantIds = []) {
        try {
          const cart = await this.getCart();
          const inCart = new Set(cart.items.map((it) => String(it.variant_id)));
          const toAdd = variantIds.filter((id) => !inCart.has(id));
          if (!toAdd.length) return;
          for (const id of toAdd) await this.addVariant(id);
        } catch (e) {
          console.error('Error adding addons: ' + e);
        }
      }

      // Removes all gift box addon products from the cart if they exist
      async ensureAddonsRemoved(variantIds = []) {
        const cart = await this.getCart();
        const toRemove = cart.items
          .filter((it) => variantIds.includes(String(it.variant_id)))
          .map((it) => it.key);
        if (!toRemove.length) return false;
        for (const key of toRemove) await this.removeByKey(key);
        return true;
      }

      // Adds all gift box addon products to the cart if they're not already there
      async ensureAddonsAdded(variantIds = []) {
        const cart = await this.getCart();
        const inCart = new Set(cart.items.map((it) => String(it.variant_id)));
        const toAdd = variantIds.filter((id) => !inCart.has(id));
        if (!toAdd.length) return false;
        for (const id of toAdd) await this.addVariant(id);
        return true;
      }

      /**
       * Remove item from the cart
       * @param {*} key
       * @returns
       */
      async removeByKey(key) {
        let body = {
          id: key,
          quantity: 0,
        };

        if (this.cartItems && this.cartItems.getSectionsToRender) {
          body.sections = this.cartItems
            .getSectionsToRender()
            .map((section) => section.section);

          body.sections_url = window.location.pathname;
        }

        return fetch(routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify(body),
        })
          .then((res) => res.text())
          .then((state) => {
            const parsedResponse = JSON.parse(state);
            // apply updates to the cart
            if (parsedResponse?.items) {
              this.items = parsedResponse?.items;
              this.item_count = parsedResponse?.item_count || 0;
            }

            if (parsedResponse?.sections) {
              this.cartItems.updateUI(parsedResponse);
            } else {
              location.reload(); // force reload to apply updates
            }
          })
          .catch((e) => {
            console.error(`Error removing key ${key}: Reason: ${e}`);
            CartUtils.showCartToast(
              e?.message || window.toastNotifications?.errorOccured,
              'error',
            );
          });
      }
      /**
       * Add gift to the cart
       * @param {*} id
       * @returns
       */
      async addVariant(id) {
        let body = {
          id: id,
          quantity: 1,
        };

        if (this.cartItems && this.cartItems.getSectionsToRender) {
          body.sections = this.cartItems
            .getSectionsToRender()
            .map((section) => section.section);

          body.sections_url = window.location.pathname;
        }

        return fetch(routes.cart_add_url, {
          ...fetchConfig(),
          body: JSON.stringify(body),
        })
          .then(async (r) => {
            const parsedResponse = await r.json();

            if (parsedResponse?.sections) {
              this.cartItems.updateUI({
                ...parsedResponse,
                item_count: this.item_count + parsedResponse.quantity,
                items_subtotal_price: this.calculateItemsSubtotalPrice(
                  parsedResponse.original_line_price,
                ),
              }); // apply ui updates
            } else {
              location.reload(); // force reload to apply updates
            }
          })
          .catch((e) => {
            console.error('Error adding variant: ' + e);
            CartUtils.showCartToast(
              e?.message || window.toastNotifications?.errorOccured,
              'error',
            );
          });
      }
      /**
       * Calculates the items subtotal price
       * @returns
       */
      calculateItemsSubtotalPrice(addedGiftPrice) {
        const currentTotal = this.items.reduce((prev, acc) => {
          return (prev += acc.final_line_price);
        }, 0);

        return currentTotal + addedGiftPrice;
      }
    },
  );
}
